import { useCallback, useEffect, useState } from "react";
import { Lock, FilePdf, Wallet, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";

function currentMonday() {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().slice(0, 10);
}

export default function PayrollPanel({ canMarkPaid = true }) {
  const [weekStart, setWeekStart] = useState(currentMonday());
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adjusting, setAdjusting] = useState(null);
  const [adjForm, setAdjForm] = useState({});
  const [adjBusy, setAdjBusy] = useState(false);

  const openAdjust = (p) => {
    setAdjusting(p);
    setAdjForm({ base_weekly: p.base_weekly, bonus: p.bonus || "", penalty: p.penalty || "", note: p.adjustment_note || "" });
  };

  const saveAdjust = async () => {
    setAdjBusy(true);
    try {
      const { data } = await api.patch(`/payroll/payslips/${adjusting.id}/adjust`, adjForm);
      toast.success(`Payslip adjusted — new total RM ${data.total.toFixed(2)}`);
      setAdjusting(null);
      loadRuns();
      loadPreview();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdjBusy(false);
    }
  };

  const adjTotal = adjusting
    ? (parseFloat(adjForm.base_weekly) || 0) + adjusting.commission_total + (parseFloat(adjForm.bonus) || 0) - (parseFloat(adjForm.penalty) || 0)
    : 0;

  const loadPreview = useCallback(() => {
    api.get(`/payroll/preview?week_start=${weekStart}`)
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [weekStart]);

  const loadRuns = useCallback(() => {
    api.get("/payroll/runs").then((r) => setRuns(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadPreview(); loadRuns(); }, [loadPreview, loadRuns]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/generate", { week_start: weekStart });
      toast.success(`Payroll locked: ${data.payslips} payslips generated for week of ${data.week_start}`);
      loadPreview();
      loadRuns();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (pid, status) => {
    try {
      await api.patch(`/payroll/payslips/${pid}`, { status });
      toast.success(`Payslip marked ${status}`);
      loadRuns();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const grandTotal = preview?.staff?.reduce((a, s) => a + s.total, 0) || 0;

  return (
    <div className="space-y-8" data-testid="payroll-panel">
      <div className="rounded-sm border border-border bg-card p-6 sm:p-8">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Week starting (7-day period)</Label>
            <Input data-testid="payroll-week-input" type="date" value={weekStart}
              onChange={(e) => setWeekStart(e.target.value)}
              className="w-52 rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <Button data-testid="payroll-generate-button" onClick={generate} disabled={busy || !preview || preview.locked}
            className="rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
            <Lock size={16} className="mr-2" />
            {preview?.locked ? "Week Locked" : busy ? "Generating..." : "Generate Weekly Payroll"}
          </Button>
          {preview && (
            <p className="text-xs text-muted-foreground">
              Period: <span className="font-mono text-foreground">{preview.week_start}</span> → <span className="font-mono text-foreground">{preview.week_end}</span>
              {preview.locked && <span className="ml-2 font-bold uppercase text-amber-400">(already generated)</span>}
            </p>
          )}
        </div>

        {preview && (
          <div className="mt-6 overflow-x-auto rounded-sm border border-border" data-testid="payroll-preview-table">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Staff</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Role</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Jobs</TableHead>
                  <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Base (Weekly)</TableHead>
                  <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Commissions</TableHead>
                  <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Total Payout</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.staff.map((s) => (
                  <TableRow key={s.user_id} className="border-border hover:bg-muted/50" data-testid={`payroll-row-${s.name.replace(/\s/g, "-")}`}>
                    <TableCell className="font-semibold">{s.name}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm font-bold uppercase tracking-wider">{s.role}</Badge></TableCell>
                    <TableCell className="font-mono">{s.job_count}</TableCell>
                    <TableCell className="text-right font-mono">RM {s.base_weekly.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono">RM {s.commission_total.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-primary">RM {s.total.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-border bg-primary/5 hover:bg-primary/5">
                  <TableCell colSpan={5} className="font-display text-sm font-bold uppercase tracking-widest">Total Payroll</TableCell>
                  <TableCell className="text-right font-mono font-black text-primary" data-testid="payroll-grand-total">RM {grandTotal.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
          <Wallet size={22} className="text-primary" /> Locked Payroll Runs
        </h3>
        {!runs.length && <p className="rounded-sm border border-dashed border-input p-8 text-center text-sm text-muted-foreground" data-testid="payroll-runs-empty">No payroll runs generated yet.</p>}
        {runs.map((r) => (
          <div key={r.id} className="rounded-sm border border-border" data-testid={`payroll-run-${r.week_start}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-5 py-3">
              <p className="font-mono text-sm font-bold text-primary">{r.week_start} → {r.week_end}</p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">by {r.created_by}</p>
            </div>
            <Table>
              <TableBody>
                {r.payslips.map((p) => (
                  <TableRow key={p.id} className="border-border hover:bg-muted/50" data-testid={`payslip-row-${p.id}`}>
                    <TableCell className="font-semibold">{p.name}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm font-bold uppercase tracking-wider">{p.role}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">RM {p.total.toFixed(2)}</TableCell>
                    <TableCell>
                      {canMarkPaid ? (
                        <Select value={p.status} onValueChange={(v) => setStatus(p.id, v)}>
                          <SelectTrigger data-testid={`payslip-status-${p.id}`}
                            className={`w-32 rounded-sm border font-bold uppercase tracking-wider ${p.status === "paid" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-popover">
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline" className={`rounded-sm font-bold uppercase ${p.status === "paid" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>{p.status}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {p.status === "pending" && canMarkPaid && (
                        <Button size="sm" variant="outline" data-testid={`payslip-edit-${p.id}`} onClick={() => openAdjust(p)}
                          className="mr-2 rounded-sm border-input hover:bg-secondary hover:text-foreground">
                          <PencilSimple size={14} className="mr-1" /> Edit
                        </Button>
                      )}
                      <Button size="sm" variant="outline" data-testid={`payslip-download-${p.id}`}
                        onClick={() => downloadFile(`/payroll/payslips/${p.id}/download`, `payslip-${p.week_start}-${p.name.replace(/\s/g, "_")}.pdf`)}
                        className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                        <FilePdf size={14} className="mr-1" /> PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>

      {adjusting && (
        <Dialog open onOpenChange={() => setAdjusting(null)}>
          <DialogContent className="border-border bg-background" data-testid="payslip-adjust-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Review & Adjust — {adjusting.name}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">
              Week {adjusting.week_start} → {adjusting.week_end} · Commissions: RM {adjusting.commission_total.toFixed(2)}
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Base Salary (Weekly)</Label>
                <Input data-testid="adjust-base" type="number" min="0" step="0.01" value={adjForm.base_weekly}
                  onChange={(e) => setAdjForm({ ...adjForm, base_weekly: e.target.value })}
                  className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Bonus (RM)</Label>
                <Input data-testid="adjust-bonus" type="number" min="0" step="0.01" value={adjForm.bonus}
                  onChange={(e) => setAdjForm({ ...adjForm, bonus: e.target.value })}
                  className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Penalty (RM)</Label>
                <Input data-testid="adjust-penalty" type="number" min="0" step="0.01" value={adjForm.penalty}
                  onChange={(e) => setAdjForm({ ...adjForm, penalty: e.target.value })}
                  className="rounded-sm border-input bg-card font-mono focus-visible:ring-primary" />
              </div>
            </div>
            <Textarea data-testid="adjust-note" value={adjForm.note} onChange={(e) => setAdjForm({ ...adjForm, note: e.target.value })}
              placeholder="Adjustment note (optional)" className="rounded-sm border-input bg-card focus-visible:ring-primary" />
            <div className="flex items-center justify-between rounded-sm border border-primary/40 bg-primary/5 px-5 py-3">
              <span className="text-xs font-bold uppercase tracking-widest text-primary">New Total Payout</span>
              <span data-testid="adjust-total" className="font-mono text-xl font-black text-primary">RM {adjTotal.toFixed(2)}</span>
            </div>
            <Button data-testid="payslip-adjust-save" onClick={saveAdjust} disabled={adjBusy}
              className="w-full rounded-sm bg-primary py-5 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
              {adjBusy ? "Saving..." : "Save & Regenerate PDF"}
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
